import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import apiService from '../services/apiService';
import './SetupAgentPage.css';
import './ViewAgentPage.css';
import './AttributeRegistryPage.css';
import {
  getAvailableEnvironments,
  getSelectedEnvironment,
  saveSelectedEnvironment,
  getApiEndpoints,
  ENVIRONMENT_LABELS,
  getEnvironmentColor
} from '../utils/apiConfig';

const formatDate = (value) => {
  if (!value) return '-';

  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return '-';

  return new Intl.DateTimeFormat('en-US', {
    month: '2-digit',
    day: '2-digit',
    year: 'numeric'
  }).format(date);
};

const RoutingDashboardPage = () => {
  const [instances, setInstances] = useState([]);
  const [policies, setPolicies] = useState([]);
  const [restrictions, setRestrictions] = useState([]);
  const [selectedInstanceId, setSelectedInstanceId] = useState('ALL');
  const [searchTerm, setSearchTerm] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [selectedEnvironment, setSelectedEnvironment] = useState(getSelectedEnvironment());

  const availableEnvironments = getAvailableEnvironments();
  const endpoints = getApiEndpoints(selectedEnvironment);

  const getUserRole = () => (
    JSON.parse(localStorage.getItem('user') || 'null')?.role || ''
  );

  const handleEnvironmentChange = (e) => {
    const environment = e.target.value;

    setSelectedEnvironment(environment);
    saveSelectedEnvironment(environment);
    setSelectedInstanceId('ALL');
    setSearchTerm('');
  };

  const filteredPolicies = policies.filter((policy) => {
    const matchesInstance = selectedInstanceId === 'ALL' || policy.instanceId === selectedInstanceId;
    const normalizedSearchTerm = searchTerm.trim().toLowerCase();

    const matchesSearch = !normalizedSearchTerm || [
      policy.policyName,
      policy.policyId,
      policy.status,
      policy.version,
      policy.instanceName
    ].some((value) => String(value || '').toLowerCase().includes(normalizedSearchTerm));

    return matchesInstance && matchesSearch;
  });

  const filteredRestrictions = restrictions.filter((restriction) => {
    const matchesInstance = selectedInstanceId === 'ALL' || restriction.instanceId === selectedInstanceId;
    const normalizedSearchTerm = searchTerm.trim().toLowerCase();

    const matchesSearch = !normalizedSearchTerm || [
      restriction.restrictionName,
      restriction.restrictionId,
      restriction.status,
      restriction.instanceName
    ].some((value) => String(value || '').toLowerCase().includes(normalizedSearchTerm));

    return matchesInstance && matchesSearch;
  });

  useEffect(() => {
    const loadDashboardData = async () => {
      setLoading(true);
      setError(null);

      try {
        const instancesResponse = await apiService.get(endpoints.instanceDropdown);
        if (instancesResponse.data.status !== 'ok') {
          throw new Error(instancesResponse.data.message || 'Failed to load instances');
        }

        const availableInstances = instancesResponse.data.instances || [];
        setInstances(availableInstances);
        const configurationResponses = await Promise.all(
          availableInstances.map(async (instance) => {
            const [policiesResponse, restrictionsResponse] = await Promise.all([
              apiService.post(endpoints.routingPolicies, {
                instanceId: instance.id,
                user_role: getUserRole()
              }),
              apiService.post(endpoints.routingRestrictions, {
                instanceId: instance.id,
                user_role: getUserRole()
              })
            ]);

            if (policiesResponse.data.status !== 'ok' || restrictionsResponse.data.status !== 'ok') {
              throw new Error(`Failed to load routing configuration for ${instance.name}`);
            }

            return {
              policies: (policiesResponse.data.result?.routingPolicies || []).map((policy) => ({
                ...policy,
                instanceName: instance.name
              })),
              restrictions: (restrictionsResponse.data.result?.routingRestrictions || []).map((restriction) => ({
                ...restriction,
                instanceName: instance.name
              }))
            };
          })
        );
        setPolicies(configurationResponses.flatMap(({ policies }) => policies));
        setRestrictions(configurationResponses.flatMap(({ restrictions }) => restrictions));
      } catch (err) {
        setError(err.response?.data?.message || err.message || 'Failed to load routing policies');
      } finally {
        setLoading(false);
      }
    };

    loadDashboardData();
  }, [endpoints.instanceDropdown, endpoints.routingPolicies, endpoints.routingRestrictions]);

  return (
    <div className="setup-page">
      <div className="page-header">
        <div>
          <h1>Routing Dashboard</h1>
          <p className="home-subtitle">View routing policy status across Connect instances</p>
        </div>
        <Link to="/routing-admin" className="btn btn-secondary">
            ← Back to Routing Administration
        </Link>
      </div>

      {/* Environment Selector */}
       <div className="environment-selector" style={{
            backgroundColor: '#f9f9f9',
            border: '2px solid #e0e0e0',
            borderRadius: '8px',
            padding: '16px',
            marginBottom: '20px',
            display: 'flex',
            alignItems: 'center',
            gap: '12px'
        }}>
        <label htmlFor="environment" style={{ 
            fontWeight: 'bold', 
            fontSize: '16px',
            minWidth: '120px'
            }}>
            Environment:
        </label>
        <select
            id="environment"
            value={selectedEnvironment}
            onChange={handleEnvironmentChange}
            style={{
                padding: '10px 16px',
                fontSize: '15px',
                borderRadius: '6px',
                border: '2px solid #ccc',
                backgroundColor: 'white',
                cursor: 'pointer',
                fontWeight: '500',
                minWidth: '200px'
            }}
            >
            {availableEnvironments.map(env => (
                <option key={env} value={env}>
                {ENVIRONMENT_LABELS[env]} ({env})
                </option>
            ))}
        </select>
        <span style={{
            padding: '6px 12px',
            borderRadius: '4px',
            fontSize: '13px',
            fontWeight: 'bold',
            color: 'white',
            backgroundColor: getEnvironmentColor(selectedEnvironment)
            }}>
            {selectedEnvironment}
        </span>
    </div>

      <div className="table-format-list">
        <div className="table-format-list-header">
          <h3>Routing Policies ({filteredPolicies.length})</h3>
          <div className="table-format-list-controls">
            <div className="page-size-selector">
              <label htmlFor="instanceFilter">Instance:</label>
              <select
                id="instanceFilter"
                value={selectedInstanceId}
                onChange={(e) => setSelectedInstanceId(e.target.value)}
              >
                <option value="ALL">All Instances</option>
                {instances.map((instance) => (
                  <option key={instance.id} value={instance.id}>
                    {instance.name}
                  </option>
                ))}
              </select>
            </div>
            <input
              type="search"
              className="table-format-search-input"
              placeholder="Search routing policies..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
          </div>
        </div>

        <table>
          <thead>
            <tr>
              <th>Policy Name</th>
              <th>Instance</th>
              <th>Status</th>
              <th>Version</th>
              <th>Updated</th>
            </tr>
          </thead>
          <tbody>
            {filteredPolicies.map((policy, index) => (
              <tr key={`${policy.instanceId}-${policy.policyId}-${index}`}>
                <td>{policy.policyName || policy.policyId || '-'}</td>
                <td>{policy.instanceName || policy.instanceId || '-'}</td>
                <td>{policy.status || '-'}</td>
                <td>{policy.version ?? '-'}</td>
                <td>{formatDate(policy.updated)}</td>
              </tr>
            ))}
          </tbody>
        </table>

        {!loading && filteredPolicies.length === 0 && (
          <div className="attribute-list">
            <p>No routing policies found.</p>
          </div>
        )}

        <div className="form-actions">
          <Link to="/routing-policies" className="btn btn-primary">
            Create Policy
          </Link>
        </div>
      </div>

      <div className="table-format-list">
        <div className="table-format-list-header">
          <h3>Routing Restrictions ({filteredRestrictions.length})</h3>
        </div>

        <table>
          <thead>
            <tr>
              <th>Restriction Name</th>
              <th>Restriction ID</th>
              <th>Instance</th>
              <th>Status</th>
            </tr>
          </thead>
          <tbody>
            {filteredRestrictions.map((restriction, index) => (
              <tr key={`${restriction.instanceId}-${restriction.restrictionId}-${index}`}>
                <td>{restriction.restrictionName || restriction.restrictionId || '-'}</td>
                <td>{restriction.restrictionId || '-'}</td>
                <td>{restriction.instanceName || restriction.instanceId || '-'}</td>
                <td>{restriction.status || '-'}</td>
              </tr>
            ))}
          </tbody>
        </table>

        {!loading && filteredRestrictions.length === 0 && (
          <div className="attribute-list">
            <p>No routing restrictions found.</p>
          </div>
        )}

        <div className="form-actions">
          <Link to="/routing-restrictions" className="btn btn-primary">
            Manage Restrictions
          </Link>
        </div>
      </div>

      {loading && (
        <div className="loading-spinner">
          <div className="spinner"></div>
          <p>Loading routing policies...</p>
        </div>
      )}

      {error && (
        <div className="alert alert-error">
          <h4>Error</h4>
          <p>{error}</p>
        </div>
      )}
    </div>
  );
};

export default RoutingDashboardPage;